import { useCallback, useEffect, useMemo, useState } from "react";
import FilterMenu from "../../../components/FilterMenu";
import Loading from "../../../components/Loading";
import {
  ChartDataParams,
  FormatChartData,
} from "../../../components/recharts/functions";
import {
  lightenColor,
  ReMixChart,
} from "../../../components/recharts/ReMixCharts";
import { SearchBox } from "../../../components/Search";
import { stringToConsistentColor } from "../../../extensions/string";
import { BrushStart, ChartType } from "../../../models/chart_model";
import { RajasthanDiscomData } from "../../../models/rajasthan";
import { fetchRvpnData, getRajasthanDiscomData } from "../Utility/RestApi";
import { ExchangeColors } from "../../Dashboard/Exchange3/FormatData";
import { formatDateForAPI, R_CHART_TYPES } from "../const";
import { DEMAND_UNITS } from "./Data";
import { getMonthFromDate } from "../../../extensions/date";
import { renderHourTick } from "../../../components/recharts/components";
import LegendSummaryTable, {
  ChartTable,
  downloadChartTableCSV,
  downloadSummaryCSV,
} from "../../../components/table/SummaryTable";
import Tooltip from "../../../components/tooltip";
import MainPageContainer from "../../../components/MainPageContainer";
import SecondaryHeader from "./SecondaryHeader";
import { useDispatch, useSelector } from "react-redux";
import NewFilterContainer from "../../../components/NewFilterContainer ";
import { AddDatesWithFileName } from "../../../helpers/helper";
import { setLoadingFilterName } from "../../../store/state/FilterLoadingState";
import BlockRangeSelector from "../../../components/BlockRangeSelector/BlockRangeSelector";
import { RajasthanSummaryAnalysis } from "./DiscomSummary";
import { RajasthanPeakAnalysis } from "./DiscomPeak";
// ▶ NEW — shared hook
import { useLegendFilter } from "../../../hooks/useLegendFilter";

// ─── constants (unchanged) ───────────────────────────────────────────────────
const GROUP_BY = ["Discom Load", "Total Discom Load", "RVPN Drawal"];
const DISCOM = [
  { label: "JDVVNL", key: "JDVVNL" },
  { label: "JVVNL", key: "JVVNL" },
  { label: "AVVNL", key: "AVVNL" },
];

let Intervals = [
  { label: "Time Slot", key: "time", values: ["block", "date"], isTime: true },
  { label: "Date", key: "date", values: ["date"], isTime: false },
  { label: "Month", key: "month", values: ["month"], isTime: false },
];

const X_AXIS_MODES = [
  { label: "Intervals", value: "intervals" },
  { label: "Peak Analysis", value: "peak" },
  { label: "Summary", value: "summary" },
];

const TYPE = ["Schedule", "Drawal", "Load Shedding", "Total Load"];

// ─── component ────────────────────────────────────────────────────────────────
export function RajasthanDiscomAnalysis() {
  const dataTab = useSelector((state: any) => state.viewMode.activeView);
  const activeTab = useSelector((state: any) => state.switchTabs.activeTab);
  const DefaultDates = useSelector(
    (state: any) => state.defaultDatesSetter.defaultDates,
  );

  const [selectedXAxisMode, setSelectedXAxisMode] = useState(X_AXIS_MODES[0]);
  const [selectedUnit, setSelectedUnit] = useState(DEMAND_UNITS[0]);
  const [data, setData] = useState<RajasthanDiscomData[]>([]);
  const [chartKeys, setChartKeys] = useState<any[]>([]);
  const [selectedDiscom, setSelectedDiscom] = useState<any[]>([]);
  const [selectedArea, setSelectedArea] = useState<any[]>([]);
  const [uniqueDate, setUniqueDate] = useState<any[]>([]);
  const [selectedDate, setSelectedDate] = useState<any[]>([]);
  const [uniqueBlock, setUniqueBlock] = useState<any[]>([]);
  const [selectedBlock, setSelectedBlock] = useState<any[]>([]);
  const [chartData, setChartData] = useState<any[]>([]);
  const [TableData, setTableData] = useState<any[]>([]);
  const [selectedChartType, setSelectedChartType] = useState(R_CHART_TYPES[2]);
  const [selectedGroupKeys, setSelectedGroupKeys] = useState(GROUP_BY[0]);
  const [selectedInterval, setSelectedInterval] = useState(Intervals[0]);
  const [loading, setLoading] = useState(true);

  const dispatch = useDispatch();

  // ─── suffix config (unchanged) ──────────────────────────────────────────
  const chartSuffixes = useMemo(
    () =>
      selectedGroupKeys === "RVPN Drawal"
        ? [
            {
              suffix: "rvpnl_drawal",
              displayName: "RVPN Drawal",
              stackSuffix: "rvpnl_drawal",
            },
          ]
        : [
            {
              suffix: "schedule",
              displayName: "Schedule",
              stackSuffix: "schedule",
            },
            { suffix: "drawal", displayName: "Drawal", stackSuffix: "drawal" },
            {
              suffix: "load_shedding",
              displayName: "Load Shedding",
              stackSuffix: "load_shedding",
            },
            {
              suffix: "total",
              displayName: "Total Load",
              stackSuffix: "total",
            },
          ],
    [selectedGroupKeys],
  );

  const [selectedSuffix, setSelectedSuffix] = useState([
    chartSuffixes[0],
    chartSuffixes[1],
  ]);

  useEffect(() => {
    if (selectedGroupKeys === "RVPN Drawal") {
      setSelectedSuffix([
        {
          suffix: "rvpnl_drawal",
          displayName: "RVPN Drawal",
          stackSuffix: "rvpnl_drawal",
        },
      ]);
    } else {
      setSelectedSuffix([chartSuffixes[0], chartSuffixes[1]]);
    }
  }, [chartSuffixes, selectedGroupKeys]);

  // ─── legend builder (unchanged logic, already a useMemo) ────────────────
  const createChartObject = (
    key: string,
    index: number,
    suffix: string,
    displayName: string,
    stackSuffix: string,
    selectedInterval: { isTime: boolean },
    selectedChartType: { value: ChartType; stackId?: string },
    ExchangeColors: string[],
  ) => ({
    dataKey: `${key}_${suffix}_${selectedUnit.toLowerCase()}`,
    name: `${key.replace("_", " ").replace("undefined", "")} ${displayName}`,
    legendColor: undefined,
    stroke:
      suffix === "schedule"
        ? lightenColor(ExchangeColors[index], 25)
        : suffix === "drawal"
          ? ExchangeColors[index]
          : stringToConsistentColor(`${key}_${suffix}`),
    type: selectedChartType.value as ChartType,
    stackId: selectedChartType.stackId !== undefined ? stackSuffix : undefined,
  });

  // `legends` = the FULL list the chart always needs
  const legends = useMemo(() => {
    return selectedSuffix.flatMap(({ suffix, displayName, stackSuffix }) =>
      chartKeys.map((key, index) =>
        createChartObject(
          key,
          index,
          suffix,
          displayName,
          stackSuffix,
          selectedInterval,
          selectedChartType,
          ExchangeColors,
        ),
      ),
    );
  }, [
    selectedSuffix,
    chartKeys,
    selectedInterval,
    selectedChartType,
    selectedUnit,
  ]);

  // ─── fetch (unchanged) ──────────────────────────────────────────────────
  const _handlefetchData = useCallback(async () => {
    if (DefaultDates[activeTab].defaultRangeDate === undefined) return;
    setLoading(true);
    const dates = DefaultDates[activeTab].defaultRangeDate;
    const startDateStr = formatDateForAPI(dates.startDate);
    const endDateStr = formatDateForAPI(dates.endDate);

    const [newData, rvpnData] = await Promise.all([
      getRajasthanDiscomData(startDateStr, endDateStr),
      fetchRvpnData(startDateStr, endDateStr),
    ]);

    let transformedBewData = newData.map((item) => ({
      ...item,
      total_mw: item.load_shedding_mw + item.drawal_mw,
    }));

    let transformedRvpnData = rvpnData.map((item: any) => {
      const { drawal_mw, ...rest } = item;
      return {
        ...rest,
        rvpnl_drawal_mwh: item.drawal_mw / 4,
        rvpnl_drawal_mw: item.drawal_mw,
        rvpnl_drawal_lu: item.drawal_mw / 400,
        block: rest.slot_id,
      };
    });

    setData(
      selectedGroupKeys === "RVPN Drawal"
        ? [...transformedRvpnData]
        : [...transformedBewData],
    );

    setUniqueDate(
      Array.from(
        new Set(
          newData
            .map((item) => item.date)
            .sort((a, b) => new Date(a).getTime() - new Date(b).getTime()),
        ),
      ),
    );
    setUniqueBlock(Array.from(new Set(newData.map((item) => item.block))));
    setLoading(false);
  }, [DefaultDates, activeTab, selectedGroupKeys]);

  useEffect(() => {
    _handlefetchData();
  }, [_handlefetchData]);

  // ─── handlers (unchanged) ───────────────────────────────────────────────
  function _handleGroupByChange(index: number) {
    setSelectedGroupKeys(GROUP_BY[index]);
    setSelectedDiscom([]);
    setSelectedArea([]);
    setSelectedDate([]);
  }

  function handleXAxisModeChange(item: string, index: number) {
    setSelectedXAxisMode(X_AXIS_MODES[index]);
  }

  function handleUnitChange(params: string) {
    setSelectedUnit(params);
  }

  function handleDiscomChange(items: string[]) {
    setSelectedDiscom(items);
  }

  function handleTypeChange(selected: string[]) {
    if (selectedGroupKeys === "RVPN Drawal") {
      dispatch(setLoadingFilterName(""));
      return;
    }
    setSelectedSuffix(
      chartSuffixes.filter((suffixObj) =>
        selected.includes(suffixObj.displayName),
      ),
    );
  }

  // ─── data processing (unchanged) ────────────────────────────────────────
  useEffect(() => {
    let filteredData = data.map((item) => {
      const dateParts = item.date.split("-");
      let monthStr = "";
      if (dateParts.length === 3) {
        const year = dateParts[0];
        const monthIndex = parseInt(dateParts[1]) - 1;
        const monthNames = [
          "Jan",
          "Feb",
          "Mar",
          "Apr",
          "May",
          "Jun",
          "Jul",
          "Aug",
          "Sep",
          "Oct",
          "Nov",
          "Dec",
        ];
        monthStr =
          monthIndex >= 0 && monthIndex < 12
            ? `${monthNames[monthIndex]}-${year.slice(-2)}`
            : getMonthFromDate(item.date);
      } else {
        monthStr = getMonthFromDate(item.date);
      }
      return { ...item, month: monthStr };
    });

    if (selectedBlock.length > 0)
      filteredData = filteredData.filter((item) =>
        selectedBlock.map((b) => b.value).includes(item.block),
      );

    if (selectedGroupKeys !== "RVPN Drawal" && selectedDiscom.length > 0)
      filteredData = data.filter((item) =>
        selectedDiscom.includes(item.discom),
      );

    if (selectedDate.length > 0)
      filteredData = filteredData.filter((item) =>
        selectedDate.map((d) => d.value).includes(item.date),
      );

    function getKeys() {
      if (selectedInterval.key === "time") return ["date", "block"];
      if (selectedInterval.key === "date") return ["date"];
      return [selectedInterval.values[0]];
    }

    const par: ChartDataParams = {
      keys: getKeys(),
      tableKeys: ["date", "block", "month"],
      groupBy: [selectedGroupKeys.toLowerCase()].concat(
        selectedGroupKeys === "Discom Load" ? ["discom"] : [],
      ),
      volumeKey: "",
      aggregationKeys:
        selectedGroupKeys === "RVPN Drawal"
          ? ["rvpnl_drawal_mw", "rvpnl_drawal_mwh", "rvpnl_drawal_lu"]
          : ["schedule", "drawal", "load_shedding", "total"].map(
              (item) => item + "_" + selectedUnit.toLowerCase(),
            ),
      averageKeys:
        selectedGroupKeys === "RVPN Drawal"
          ? ["rvpnl_drawal_mw"]
          : ["schedule_mw", "drawal_mw", "load_shedding_mw", "total_mw"],
      averageDuplicationKeys: ["date", "block"],
      priceKey: "",
      data: filteredData,
    };

    const cData = FormatChartData(par);
    setChartData(cData.data);
    setChartKeys(cData.keys);
    setTableData(cData.tableData || []);
  }, [
    data,
    selectedGroupKeys,
    selectedInterval,
    selectedDiscom,
    selectedArea,
    selectedDate,
    selectedBlock,
    selectedSuffix,
    selectedUnit,
  ]);

  // ─── x-axis helpers (unchanged) ─────────────────────────────────────────
  const handlexDataKey = useCallback(() => {
    if (selectedInterval.key === "time") return "block";
    return selectedInterval.key;
  }, [selectedBlock, selectedInterval]);

  const xDataKey = useMemo(() => handlexDataKey(), [handlexDataKey]);

  function secondDataKey() {
    return selectedInterval.isTime ? "date" : undefined;
  }

  // ─── table params ───────────────────────────────────────────────────────
  const summaryExtraKeys = selectedInterval.isTime
    ? ["block", selectedInterval.values[1]]
    : [selectedInterval.values[0]];

  const summaryExtraLabels = selectedInterval.values.map((value) =>
    value === "date" ? "Date" : value,
  );

  // ▶ NEW — drop in the hook (replaces activeLegends + its handleLegendSelect)
  const { visibleLegends, handleLegendSelect, selectedLegends } =
    useLegendFilter(legends, [chartData]);

  const tablePar = {
    legends: visibleLegends, // ← was: legends
    data: chartData,
    tableData: TableData,
    itemsPerPage: 96,
    unit: selectedUnit,
    extraKeys: summaryExtraKeys,
    extraLabels: summaryExtraLabels,
    filename: AddDatesWithFileName({
      fileName: "Discom Load",
      dates: DefaultDates[activeTab].defaultRangeDate,
    }),
  };

  // ─── sub-page routes (unchanged) ────────────────────────────────────────
  if (selectedXAxisMode.value === "peak") {
    return (
      <RajasthanPeakAnalysis
        onXAxisModeChange={handleXAxisModeChange}
        selectedUnit={selectedUnit}
        onUnitChange={handleUnitChange}
        selectedDiscom={selectedDiscom}
        onDiscomChange={handleDiscomChange}
        selectedGroupKeys={selectedGroupKeys}
        onGroupByChange={_handleGroupByChange}
        selectedSuffix={selectedSuffix}
        onTypeChange={handleTypeChange}
        chartSuffixes={chartSuffixes}
      />
    );
  }

  if (selectedXAxisMode.value === "summary") {
    return (
      <RajasthanSummaryAnalysis
        onXAxisModeChange={handleXAxisModeChange}
        selectedUnit={selectedUnit}
        onUnitChange={handleUnitChange}
        selectedDiscom={selectedDiscom}
        onDiscomChange={handleDiscomChange}
        selectedGroupKeys={selectedGroupKeys}
        onGroupByChange={_handleGroupByChange}
        selectedSuffix={selectedSuffix}
        onTypeChange={handleTypeChange}
        chartSuffixes={chartSuffixes}
      />
    );
  }

  // ─── render ─────────────────────────────────────────────────────────────
  return (
    <>
      <div className="gas-page-container">
        {loading && <Loading />}
        <NewFilterContainer>
          <div className="side-filters-container text-nowrap">
            <FilterMenu
              title="X-Axis"
              items={X_AXIS_MODES.map((item) => item.label)}
              selectedItems={[selectedXAxisMode.label]}
              onSelect={handleXAxisModeChange}
            />
            <FilterMenu
              title="Intervals"
              items={Intervals.map((item) => item.label)}
              selectedItems={[selectedInterval.label]}
              onSelect={(item, index) => setSelectedInterval(Intervals[index])}
            />
            {selectedGroupKeys !== "RVPN Drawal" && (
              <FilterMenu
                title="Discom"
                items={DISCOM.map((item) => item.label)}
                selectedItems={selectedDiscom}
                onSelect={() => {}}
                setSelectedItems={(item) => {
                  if (selectedGroupKeys === "RVPN Drawal") {
                    dispatch(setLoadingFilterName(""));
                    return;
                  }
                  setSelectedDiscom(item);
                }}
              />
            )}
            <FilterMenu
              title="Unit"
              items={DEMAND_UNITS}
              selectedItems={[selectedUnit]}
              onSelect={(item) => handleUnitChange(item)}
            />
            <FilterMenu
              title="Group By"
              items={GROUP_BY}
              selectedItems={[selectedGroupKeys]}
              onSelect={(item, index) => _handleGroupByChange(index)}
            />
            {selectedGroupKeys !== "RVPN Drawal" && (
              <FilterMenu
                title="Type"
                items={TYPE}
                selectedItems={selectedSuffix.map((item) => item.displayName)}
                setSelectedItems={handleTypeChange}
              />
            )}
            <FilterMenu
              title="Chart Type"
              items={R_CHART_TYPES.map((item) => item.label)}
              selectedItems={[selectedChartType.label]}
              onSelect={(item, index) =>
                setSelectedChartType(R_CHART_TYPES[index])
              }
            />
          </div>
        </NewFilterContainer>

        <MainPageContainer>
          <SecondaryHeader />
          <div className="gas-chart-container">
            <div className="gas-heading gap-x-10">
              <h2 className="chartHeading w-60">
                {selectedGroupKeys} ({selectedUnit})
              </h2>
              <div className="flex gap-x-6 justify-end">
                <BlockRangeSelector
                  blocks={Array.from({ length: 96 }, (_, i) => i + 1)}
                  placeholder="Search Block"
                  selectedOptions={selectedBlock.map((item) => item.value)}
                  setSelectedOptions={setSelectedBlock}
                />
                <button
                  onClick={() => {
                    dataTab.label === "Chart" &&
                      downloadChartTableCSV(tablePar);
                    dataTab.label === "Summary" && downloadSummaryCSV(tablePar);
                    dataTab.label === "Table" &&
                      downloadChartTableCSV(tablePar);
                  }}
                  className="downloadButton"
                >
                  Download Data
                </button>
              </div>
            </div>

            {/* ▶ CHART — added defaultSelectedLegends, handleLegendSelect is now from the hook */}
            {dataTab.label === "Chart" && (
              <ReMixChart
                key={`${selectedGroupKeys}-${selectedUnit}-${selectedInterval.key}-${selectedChartType.value}`}
                data={chartData}
                showBrush={chartData.length > 96 ? true : undefined}
                brushStart={BrushStart.Start}
                isTimeSlot={selectedInterval.isTime}
                onlyTitle={true}
                barCategoryGap={10}
                unit={selectedUnit}
                yAxisLabel={selectedUnit}
                legends={legends} // ← FULL list (chart needs it)
                onLegendSelect={handleLegendSelect} // ← from hook
                defaultSelectedLegends={selectedLegends} // ← NEW: keeps chart synced
                xDataKey={xDataKey}
                xTick={
                  selectedInterval.isTime && selectedBlock.length === 0
                    ? (value: any, index: any) => renderHourTick(value, 10)
                    : undefined
                }
                secondXDataKey={secondDataKey()}
              />
            )}

            {/* ▶ TABLE & SUMMARY — tablePar already has visibleLegends, no change here */}
            {dataTab.label === "Summary" && (
              <LegendSummaryTable {...tablePar} />
            )}
            {dataTab.label === "Table" && <ChartTable {...tablePar} />}
          </div>

          <div className="gna-footer-container">
            <div className="grid grid-cols-3 justify-between gap-5">
              <Tooltip
                note={
                  <p>
                    DISCOM Energy Reports (RUVITL), Load Shedding Reports
                    (RUVITL)
                  </p>
                }
                text="1. Actual Drawal = Restricted Demand&#10;   Total Load (Unrestricted Demand) = Actual Drawal + Load Shedding&#10;2. Load Shedding has been distributed across time blocks based on the actual duration of load shedding&#10;3. MW = Represents the average (MW) when the selected time interval is Date or Month."
              >
                <p className="gna-footer-text">Source</p>
              </Tooltip>
              <p className="gna-footer-text col-span-1 text-center">
                Powered by <strong>GNA Energy</strong>
              </p>
              <strong className="gna-footer-text col-span-1 text-end">
                Beta Version
              </strong>
            </div>
          </div>
        </MainPageContainer>
      </div>
    </>
  );
}
